import React, { useState, useEffect } from 'react';
import {
  FloatingDrawer,
  FloatingDrawerHeader,
  FloatingDrawerBody,
  FloatingDrawerFooter,
} from '@/components/ui/floating-drawer';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Shield, ChevronRight } from 'lucide-react';
import { TeamMember } from '@/lib/api/services/teamService';
import { primaryMenuItems, standaloneNavItems } from '@/components/layout/AppSidebar';
import { isSectionGranted } from '@/lib/menuPermissions.core';
import { useToast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors/showApiError';

// Map sidebar menu IDs to a permission-dialog group label. Any ID added to
// AppSidebar that's not listed here falls under "Other" so a newly-shipped
// feature is always visible to admins assigning permissions, even before
// this map gets a curated entry.
const GROUP_BY_ID: Record<string, string> = {
  overview: 'General',
  inbox: 'General',
  marketing: 'Marketing',
  'social-media': 'Marketing',
  customers: 'Marketing',
  automation: 'Marketing',
  ads: 'Marketing',
  instagram: 'Marketing',
  youtube: 'Marketing',
  scheduling: 'Operations',
  store: 'Operations',
  'commerce-v2': 'Operations',
  'website-builder': 'Operations',
  'data-store': 'Operations',
  petpooja: 'Operations',
  reports: 'Analytics',
  'ai-tools': 'Tools',
  settings: 'Admin',
  help: 'Admin',
};

const GROUP_ORDER = ['General', 'Marketing', 'Operations', 'Analytics', 'Tools', 'Admin', 'Other'];

const standaloneIdFromUrl = (url: string): string => {
  if (url === '/home') return 'overview';
  if (url === '/home/inbox') return 'inbox';
  return url;
};

type MenuEntry = { id: string; label: string; group: string };

const MENU_ITEMS: MenuEntry[] = [
  ...standaloneNavItems.map((i) => {
    const id = standaloneIdFromUrl(i.url);
    return { id, label: i.title, group: GROUP_BY_ID[id] || 'Other' };
  }),
  ...primaryMenuItems.map((i) => ({
    id: i.id,
    label: i.title,
    group: GROUP_BY_ID[i.id] || 'Other',
  })),
  // Admin sections (Account Settings, Support) are intentionally NOT grantable
  // here: their admin sub-pages are role-locked (never available to USER/AGENT),
  // and General Settings + Support are always available regardless of permissions.
  // Showing them as toggles would be misleading — toggling them does nothing.
].filter((i) => i.group !== 'Admin');

const GROUPS = GROUP_ORDER.filter((g) => MENU_ITEMS.some((i) => i.group === g));

const ALL_IDS = new Set(MENU_ITEMS.map(i => i.id));

/**
 * Pre-check sections a stored array grants only through a legacy alias.
 *
 * A member whose `menu_permissions` predates a group split names just the old id
 * (e.g. 'marketing' before Social Media Posts became its own 'social-media'
 * group). `isSectionGranted` already honours that at runtime, so the member DOES
 * have access — the checkbox must show it, otherwise the dialog lies, and saving
 * an unchanged form would look like it revoked something. Writing the expanded
 * set on save also makes the grant explicit, so the alias stops being load-bearing
 * for that member.
 */
const expandLegacySectionIds = (perms: string[]): string[] => {
  const next = new Set(perms);
  for (const { id } of MENU_ITEMS) {
    if (isSectionGranted(id, perms)) next.add(id);
  }
  return Array.from(next);
};

interface MemberPermissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: TeamMember;
  onSave: (memberId: string, menuPermissions: string[] | null) => Promise<void>;
}

export const MemberPermissionsDialog: React.FC<MemberPermissionsDialogProps> = ({
  open,
  onOpenChange,
  member,
  onSave,
}) => {
  const { toast } = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set(ALL_IDS));
  const [saving, setSaving] = useState(false);

  const getMenuPermissions = (m: TeamMember): string[] | null => {
    let prefs = (m as any).preferences;
    if (typeof prefs === 'string') {
      try { prefs = JSON.parse(prefs); } catch { prefs = null; }
    }
    const mp = prefs?.menu_permissions ?? (m as any).menu_permissions;
    return Array.isArray(mp) ? mp : null;
  };

  useEffect(() => {
    if (open) {
      const perms = getMenuPermissions(member);
      setSelected(perms ? new Set(expandLegacySectionIds(perms)) : new Set(ALL_IDS));
    }
  }, [open, member]);

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleGroup = (group: string) => {
    const groupIds = MENU_ITEMS.filter(i => i.group === group).map(i => i.id);
    const allChecked = groupIds.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      groupIds.forEach(id => allChecked ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(ALL_IDS));
  const deselectAll = () => setSelected(new Set());

  /**
   * Which group sections are expanded. A Set (not a single id) so groups open
   * independently — an admin comparing Marketing against Operations should not
   * have one snap shut because they opened the other. Reset on close so the
   * drawer always reopens in the same compact state.
   */
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const setGroupOpen = (group: string, next: boolean) => {
    setOpenGroups(prev => {
      const updated = new Set(prev);
      if (next) updated.add(group);
      else updated.delete(group);
      return updated;
    });
  };
  useEffect(() => {
    if (!open) setOpenGroups(new Set());
  }, [open]);

  // Count only menu sections (not integration sub-permissions) so the
  // "N of M sections enabled" label can never exceed MENU_ITEMS.length.
  const selectedSectionCount = MENU_ITEMS.filter(i => selected.has(i.id)).length;

  const allSelected = MENU_ITEMS.every(i => selected.has(i.id));

  const handleSave = async () => {
    if (saving) return;

    // Always save the explicit full array for proper role override.
    // null would mean "use role defaults" which is limited for agents.
    // We want explicit full access to override role defaults.
    const all = Array.from(selected);

    // Strip any stale/unknown ids that may have been seeded from the member's
    // stored permissions — a sidebar section that was since renamed/removed, or
    // an integration key dropped from INTEGRATION_CONFIGS. These must NEVER block
    // a legitimate save (that was the "Save button does nothing" bug); drop them
    // silently and keep going.
    const permissions = all.filter(id => ALL_IDS.has(id));
    const dropped = all.filter(id => !ALL_IDS.has(id));
    if (dropped.length > 0) {
      console.warn('[MemberPermissions] dropping unknown permission ids:', dropped);
    }

    // Backend rejects an empty array (null = full access, [] is not allowed).
    // Surface this to the user instead of silently throwing.
    if (permissions.length === 0) {
      toast({
        title: 'Select at least one section',
        description: 'Grant at least one section, or click "Select All" for full access.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      await onSave(member.id, permissions);
      onOpenChange(false);
    } catch (error: any) {
      // onSave handles its own toasts on the happy/expected-error paths; this
      // is a safety net so an unexpected throw never silently kills the button
      // or leaks an unhandled promise rejection.
      console.error('Error saving permissions:', error);
      showApiError(error, { fallback: { title: "Couldn't save permissions", description: 'Failed to save permissions. Please try again.' } });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Never dismiss mid-save — the write may already have landed, and the parent
   * runs a conflict check on the next open that a half-finished save confuses.
   */
  const handleOpenChange = (next: boolean) => {
    if (!next && saving) return;
    onOpenChange(next);
  };

  return (
    <FloatingDrawer
      open={open}
      onOpenChange={handleOpenChange}
      side="right"
      width={480}
      className="overflow-hidden"
    >
      <FloatingDrawerHeader
        title="Menu permissions"
        subtitle={
          <span className="truncate">
            Sidebar sections <strong className="font-semibold text-[#101828]">{member.name}</strong>{' '}
            can access
          </span>
        }
        icon={<Shield className="h-4 w-4" />}
      />

      {/* The dialog put header, body AND footer inside one `overflow-y-auto`
          box, so the actions scrolled away on a long section list. In the
          drawer the body is the only scroll region and the footer is pinned. */}
      <FloatingDrawerBody className="space-y-4">
          {/* Select all / Deselect all */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {selectedSectionCount} of {MENU_ITEMS.length} sections enabled
              {allSelected && <span className="ml-1 text-green-600">(full access)</span>}
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={selectAll}>
                Select All
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={deselectAll}>
                Deselect All
              </Button>
            </div>
          </div>

          {/* Menu list — one collapsible section per group, mirroring the
              sidebar this dialog configures (AppSidebar uses the same
              Collapsible + rotating ChevronRight). Collapsed by default so a
              15-section list is scannable; the `checked/total` count is what
              keeps a collapsed group informative, and is also why the group
              checkbox stays a plain boolean — the shared Checkbox renders a tick
              for `indeterminate` too, so a count says "partial" far more
              clearly than the control could. */}
          <div className="space-y-2">
            {GROUPS.map(group => {
              const groupItems = MENU_ITEMS.filter(i => i.group === group);
              const checkedCount = groupItems.filter(i => selected.has(i.id)).length;
              const allChecked = checkedCount === groupItems.length;
              const someChecked = checkedCount > 0;
              const isOpen = openGroups.has(group);

              return (
                <Collapsible
                  key={group}
                  open={isOpen}
                  onOpenChange={(next) => setGroupOpen(group, next)}
                  className="group/collapsible rounded-lg border border-[#EAECF0]"
                >
                  {/* The checkbox is a SIBLING of the trigger, never inside it:
                      Radix renders both as <button>, and nesting them would be
                      invalid HTML AND make ticking a group also expand it. */}
                  <div className="flex items-center gap-2 px-3 py-2.5">
                    <Checkbox
                      checked={allChecked}
                      ref={(el) => {
                        if (el) (el as any).indeterminate = !allChecked && someChecked;
                      }}
                      onCheckedChange={() => toggleGroup(group)}
                      aria-label={`Grant every section in ${group}`}
                      data-testid={`perm-group-checkbox-${group}`}
                    />
                    <CollapsibleTrigger
                      className="flex flex-1 items-center gap-2 text-left"
                      data-testid={`perm-group-trigger-${group}`}
                    >
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {group}
                      </span>
                      <span
                        className="text-[11px] tabular-nums text-muted-foreground"
                        data-testid={`perm-group-count-${group}`}
                      >
                        {checkedCount}/{groupItems.length}
                      </span>
                      <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent>
                    <div className="space-y-2 px-3 pb-3 pl-9">
                      {groupItems.map(item => (
                        <div key={item.id} className="flex items-center gap-2">
                          <Checkbox
                            id={item.id}
                            checked={selected.has(item.id)}
                            onCheckedChange={() => toggle(item.id)}
                          />
                          <Label htmlFor={item.id} className="cursor-pointer text-sm">
                            {item.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
      </FloatingDrawerBody>

      <FloatingDrawerFooter>
        <Button
          variant="outline"
          className="rounded-[8px]"
          onClick={() => handleOpenChange(false)}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Permissions'}
        </Button>
      </FloatingDrawerFooter>
    </FloatingDrawer>
  );
};
